// Tutorial overlay - renders highlights and tooltips
import React, { useState, useEffect, useRef } from 'react'
import { useTutorial } from './TutorialProvider'

// ============================================================================
// HighlightBorder Component (just the border, no mask)
// ============================================================================

interface HighlightBorderProps {
  targetElementId: string
  padding?: number
}

const HighlightBorder: React.FC<HighlightBorderProps> = ({
  targetElementId,
  padding = 8,
}) => {
  const [position, setPosition] = useState<{
    rect: DOMRect
    transform: string
  } | null>(null)

  useEffect(() => {
    const updatePosition = () => {
      const element = document.querySelector(`[data-tutorial-id="${targetElementId}"]`) as HTMLElement
      if (element) {
        const rect = element.getBoundingClientRect()
        // Get the computed transform from the element
        const computedStyle = window.getComputedStyle(element)
        const transform = computedStyle.transform
        setPosition({ rect, transform })
      } else {
        setPosition(null)
      }
    }

    updatePosition()

    // Update on resize and scroll
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    // Update periodically in case elements animate/move
    const interval = setInterval(updatePosition, 100)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      clearInterval(interval)
    }
  }, [targetElementId])

  if (!position) return null

  const { rect, transform } = position
  const { top, left, width, height } = rect
  const p = padding

  return (
    <div
      style={{
        position: 'fixed',
        top: top - p,
        left: left - p,
        width: width + p * 2,
        height: height + p * 2,
        border: '2px solid #fbbf24',
        borderRadius: 8,
        boxShadow: '0 0 20px rgba(251, 191, 36, 0.4), inset 0 0 20px rgba(251, 191, 36, 0.2)',
        pointerEvents: 'none',
        transition: 'all 0.3s ease',
        zIndex: 9998,
        animation: 'highlight-pulse 2s ease-in-out infinite',
        // Apply the same transform as the target element
        transform: transform !== 'none' ? transform : undefined,
        // Set transform origin to match the card's transform origin
        transformOrigin: 'bottom center',
      }}
    />
  )
}

// ============================================================================
// MultiHighlightMask Component (multiple cutouts)
// ============================================================================

interface MultiHighlightMaskProps {
  targetElementIds: string[]
  padding?: number
  overlayOpacity?: number
}

const MultiHighlightMask: React.FC<MultiHighlightMaskProps> = ({
  targetElementIds,
  padding = 12,
  overlayOpacity = 0.75,
}) => {
  const [cutouts, setCutouts] = useState<Array<{ id: string; rect: DOMRect; rotationAngle?: number }>>([])

  useEffect(() => {
    const updateCutouts = () => {
      const results: Array<{ id: string; rect: DOMRect; rotationAngle?: number }> = []
      const originalHovers: Array<{ element: HTMLElement; transform: string }> = []

      // First pass: Remove hover effects and store original transforms
      for (const id of targetElementIds) {
        const element = document.querySelector(`[data-tutorial-id="${id}"]`) as HTMLElement
        if (element) {
          const parentElement = element.parentElement as HTMLElement
          if (parentElement) {
            const originalTransform = parentElement.style.transform
            originalHovers.push({ element: parentElement, transform: originalTransform })

            // Remove translateY from transform (keep only rotation)
            const rotateMatch = originalTransform.match(/rotate\(([-\d.]+)deg\)/)
            if (rotateMatch) {
              parentElement.style.transform = `rotate(${rotateMatch[1]}deg)`
            }
          }
        }
      }

      // Force a reflow to apply transform changes
      document.body.offsetHeight

      // Second pass: Calculate positions without hover effect
      for (const id of targetElementIds) {
        const element = document.querySelector(`[data-tutorial-id="${id}"]`) as HTMLElement
        if (element) {
          const rect = element.getBoundingClientRect()
          const parentElement = element.parentElement as HTMLElement
          let rotationAngle = 0

          if (parentElement) {
            const transform = parentElement.style.transform || ''
            const rotateMatch = transform.match(/rotate\(([-\d.]+)deg\)/)
            if (rotateMatch) {
              rotationAngle = Math.abs(parseFloat(rotateMatch[1]))
            }
          }

          // Expand for rotation
          const angleRad = (rotationAngle * Math.PI) / 180
          const expansionFactor = Math.abs(Math.cos(angleRad)) + Math.abs(Math.sin(angleRad))
          const extraPadding = (Math.max(rect.width, rect.height) * (expansionFactor - 1)) / 2

          results.push({
            id,
            rect: {
              x: rect.x - extraPadding,
              y: rect.y - extraPadding,
              width: rect.width + extraPadding * 2,
              height: rect.height + extraPadding * 2,
              top: rect.top - extraPadding,
              left: rect.left - extraPadding,
              right: rect.right + extraPadding,
              bottom: rect.bottom + extraPadding,
              toJSON: rect.toJSON,
            },
            rotationAngle,
          })
        }
      }

      // Restore original transforms
      for (const { element, transform } of originalHovers) {
        element.style.transform = transform
      }

      setCutouts(results)
    }

    updateCutouts()

    // Update on resize and scroll
    window.addEventListener('resize', updateCutouts)
    window.addEventListener('scroll', updateCutouts, true)

    // Update periodically in case elements animate/move
    const interval = setInterval(updateCutouts, 100)

    return () => {
      window.removeEventListener('resize', updateCutouts)
      window.removeEventListener('scroll', updateCutouts, true)
      clearInterval(interval)
    }
  }, [targetElementIds])

  // If no cutouts, render full overlay
  if (cutouts.length === 0) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          opacity: overlayOpacity,
          pointerEvents: 'none',
          zIndex: 9997,
          transition: 'opacity 0.3s ease',
        }}
      />
    )
  }

  // Calculate a single combined bounding box for all cutouts
  const minTop = Math.min(...cutouts.map((c) => c.rect.top))
  const minLeft = Math.min(...cutouts.map((c) => c.rect.left))
  const maxBottom = Math.max(...cutouts.map((c) => c.rect.bottom))
  const maxRight = Math.max(...cutouts.map((c) => c.rect.right))

  const combinedRect = {
    top: minTop,
    left: minLeft,
    width: maxRight - minLeft,
    height: maxBottom - minTop,
  }

  // Use SVG mask for a single combined cutout
  const maskId = `tutorial-mask-${Math.random().toString(36).substr(2, 9)}`

  return (
    <svg
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9997,
      }}
    >
      <defs>
        <mask id={maskId}>
          {/* White = visible (overlay), Black = transparent (cutout) */}
          <rect width="100%" height="100%" fill="white" />
          <rect
            x={Math.max(0, combinedRect.left - padding)}
            y={Math.max(0, combinedRect.top - padding)}
            width={combinedRect.width + padding * 2}
            height={combinedRect.height + padding * 2}
            rx={12}
            fill="black"
          />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(0, 0, 0, 0.75)"
        opacity={overlayOpacity}
        mask={`url(#${maskId})`}
      />

      {/* Note: Golden borders are now rendered on the cards themselves via isTutorialHighlighted prop */}
    </svg>
  )
}

// ============================================================================
// HighlightMask Component (four-div approach for single highlight)
// ============================================================================

interface HighlightMaskProps {
  targetElementId: string | null
  padding?: number
  overlayOpacity?: number
}

const HighlightMask: React.FC<HighlightMaskProps> = ({
  targetElementId,
  padding = 12,
  overlayOpacity = 0.75,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    const updateRect = () => {
      if (!targetElementId) {
        setTargetRect(null)
        return
      }

      const element = document.querySelector(`[data-tutorial-id="${targetElementId}"]`)
      if (element) {
        setTargetRect(element.getBoundingClientRect())
      } else {
        setTargetRect(null)
      }
    }

    updateRect()

    // Update on resize and scroll
    window.addEventListener('resize', updateRect)
    window.addEventListener('scroll', updateRect, true)

    // Update periodically in case elements animate/move
    const interval = setInterval(updateRect, 100)

    return () => {
      window.removeEventListener('resize', updateRect)
      window.removeEventListener('scroll', updateRect, true)
      clearInterval(interval)
    }
  }, [targetElementId])

  // If no target, render full overlay
  if (!targetRect) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          opacity: overlayOpacity,
          pointerEvents: 'none',
          zIndex: 9997,
          transition: 'opacity 0.3s ease',
        }}
      />
    )
  }

  const { top, left, width, height } = targetRect
  const p = padding

  // Calculate the four overlay regions
  const topHeight = Math.max(0, top - p)
  const bottomTop = top + height + p
  const leftWidth = Math.max(0, left - p)
  const rightLeft = left + width + p
  const middleHeight = height + p * 2

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9997,
      }}
    >
      {/* Top overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: topHeight,
          background: 'rgba(0, 0, 0, 0.75)',
          opacity: overlayOpacity,
          transition: 'all 0.3s ease',
        }}
      />

      {/* Left overlay */}
      <div
        style={{
          position: 'fixed',
          top: topHeight,
          left: 0,
          width: leftWidth,
          height: middleHeight,
          background: 'rgba(0, 0, 0, 0.75)',
          opacity: overlayOpacity,
          transition: 'all 0.3s ease',
        }}
      />

      {/* Right overlay */}
      <div
        style={{
          position: 'fixed',
          top: topHeight,
          left: rightLeft,
          right: 0,
          height: middleHeight,
          background: 'rgba(0, 0, 0, 0.75)',
          opacity: overlayOpacity,
          transition: 'all 0.3s ease',
        }}
      />

      {/* Bottom overlay */}
      <div
        style={{
          position: 'fixed',
          top: bottomTop,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          opacity: overlayOpacity,
          transition: 'all 0.3s ease',
        }}
      />

      {/* Highlight border around cutout */}
      <div
        style={{
          position: 'fixed',
          top: top - p,
          left: left - p,
          width: width + p * 2,
          height: height + p * 2,
          border: '2px solid #fbbf24',
          borderRadius: 12,
          boxShadow: '0 0 20px rgba(251, 191, 36, 0.25)',
          pointerEvents: 'none',
          transition: 'all 0.3s ease',
          zIndex: 9998,
        }}
      />
    </div>
  )
}

// ============================================================================
// TooltipBubble Component
// ============================================================================

interface TooltipBubbleProps {
  title?: string
  content: string | { html: string }
  position: 'top' | 'bottom' | 'left' | 'right' | 'center'
  targetElement: string | null
  showControls: boolean
  currentStep: number
  totalSteps: number
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
  canGoPrev: boolean
  canGoNext: boolean
}

const TooltipBubble: React.FC<TooltipBubbleProps> = ({
  title,
  content,
  position,
  targetElement,
  showControls,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  canGoPrev,
  canGoNext,
}) => {
  // Helper to render content (string or HTML)
  const renderContent = () => {
    if (typeof content === 'string') {
      return <p style={{
        margin: '0 0 16px 0',
        fontSize: 14,
        lineHeight: 1.6,
        color: 'rgba(255, 255, 255, 0.9)',
      }}>{content}</p>
    }
    return <div
      dangerouslySetInnerHTML={{ __html: content.html }}
      style={{
        margin: '0 0 16px 0',
        fontSize: 14,
        lineHeight: 1.6,
        color: 'rgba(255, 255, 255, 0.9)',
      }}
    />
  }
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const updatePosition = () => {
      if (!tooltipRef.current) return

      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const viewport = { width: window.innerWidth, height: window.innerHeight }

      // Center position (no target element)
      if (!targetElement || position === 'center') {
        setTooltipPos({
          x: (viewport.width - tooltipRect.width) / 2,
          y: (viewport.height - tooltipRect.height) / 2,
        })
        return
      }

      // Get target element position
      const targetEl = document.querySelector(`[data-tutorial-id="${targetElement}"]`)
      if (!targetEl) {
        setTooltipPos({
          x: (viewport.width - tooltipRect.width) / 2,
          y: (viewport.height - tooltipRect.height) / 2,
        })
        return
      }

      const targetRect = targetEl.getBoundingClientRect()
      const gap = 16 // Gap between tooltip and target

      let x = 0,
        y = 0

      switch (position) {
        case 'top':
          x = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2
          y = targetRect.top - tooltipRect.height - gap
          break
        case 'bottom':
          x = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2
          y = targetRect.bottom + gap
          break
        case 'left':
          x = targetRect.left - tooltipRect.width - gap
          y = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2
          break
        case 'right':
          x = targetRect.right + gap
          y = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2
          break
      }

      // Clamp to viewport
      x = Math.max(16, Math.min(x, viewport.width - tooltipRect.width - 16))
      y = Math.max(16, Math.min(y, viewport.height - tooltipRect.height - 16))

      setTooltipPos({ x, y })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    return () => window.removeEventListener('resize', updatePosition)
  }, [targetElement, position])

  return (
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        left: tooltipPos.x,
        top: tooltipPos.y,
        maxWidth: 380,
        minWidth: 280,
        background: 'rgba(30, 30, 35, 0.98)',
        backdropFilter: 'blur(20px)',
        borderRadius: 16,
        border: '1px solid rgba(251, 191, 36, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        padding: 20,
        zIndex: 9999,
        transition: 'left 0.3s ease, top 0.3s ease',
      }}
    >
      {/* Title */}
      {title && (
        <h3
          style={{
            margin: '0 0 12px 0',
            fontSize: 18,
            fontWeight: 600,
            color: '#fbbf24',
            letterSpacing: '0.02em',
          }}
        >
          {title}
        </h3>
      )}

      {/* Content */}
      {renderContent()}

      {/* Controls */}
      {showControls && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 12,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Step indicator */}
          <span
            style={{
              fontSize: 12,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          >
            {currentStep}/{totalSteps}
          </span>

          {/* Navigation buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Skip button */}
            <button
              onClick={onSkip}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 8,
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: 12,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
              }}
            >
              Skip
            </button>

            {/* Previous button */}
            {canGoPrev && (
              <button
                onClick={onPrev}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 12,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                }}
              >
                Back
              </button>
            )}

            {/* Next button */}
            <button
              onClick={onNext}
              style={{
                padding: '6px 16px',
                background: 'linear-gradient(135deg, #fbbf24, #d97706)',
                border: 'none',
                borderRadius: 8,
                color: '#000',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: '0 2px 8px rgba(251, 191, 36, 0.3)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.4)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(251, 191, 36, 0.3)'
              }}
            >
              {currentStep === totalSteps ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// TutorialOverlay Component
// ============================================================================

const TutorialOverlay: React.FC = () => {
  const {
    currentStep,
    totalSteps,
    currentStepData,
    nextStep,
    prevStep,
    exitTutorial,
    canGoPrev,
    canGoNext,
  } = useTutorial()

  const { tooltip, highlights, backdropOpacity = 0.75 } = currentStepData

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'ArrowRight') {
        e.preventDefault()
        if (canGoNext) nextStep()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        if (canGoPrev) prevStep()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        exitTutorial()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canGoNext, canGoPrev, nextStep, prevStep, exitTutorial])

  // Separate highlights into mask (spotlight) and glow (individual borders)
  const maskHighlights = highlights.filter((h) => h.type === 'mask')
  const glowHighlights = highlights.filter((h) => h.type === 'glow')

  return (
    <>
      {/* CSS animation for pulse effect */}
      <style>{`
        @keyframes highlight-pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.02);
          }
        }
      `}</style>

      {/* Highlight mask (spotlight effect) */}
      {maskHighlights.length === 1 ? (
        <HighlightMask
          targetElementId={maskHighlights[0].elementId}
          overlayOpacity={backdropOpacity}
        />
      ) : maskHighlights.length > 1 ? (
        <MultiHighlightMask
          targetElementIds={maskHighlights.map((h) => h.elementId)}
          overlayOpacity={backdropOpacity}
        />
      ) : glowHighlights.length > 0 ? (
        // Full backdrop if no mask highlight but we have glow highlights
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            opacity: backdropOpacity,
            pointerEvents: 'none',
            zIndex: 9997,
            transition: 'opacity 0.3s ease',
          }}
        />
      ) : null}

      {/* Individual glow highlights (borders around multiple elements) */}
      {glowHighlights.map((highlight) => (
        <HighlightBorder
          key={highlight.elementId}
          targetElementId={highlight.elementId}
        />
      ))}

      {/* Tooltip */}
      <TooltipBubble
        title={tooltip.title}
        content={tooltip.content}
        position={tooltip.position}
        targetElement={tooltip.targetElement}
        showControls={tooltip.showControls}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={exitTutorial}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
      />
    </>
  )
}

export default TutorialOverlay
